import { Router } from 'expo-router';

import { routes } from 'navigation/routing';

/**
 * iOS sheet dismissal is a native animation. Navigating while it runs leaves the
 * sheet presented on top of the next screen. The unmounted sheet emits no
 * `transitionEnd` to wait on (only `beforeRemove`), so a fixed delay is used.
 */

export const IOS_MODAL_DISMISS_DELAY_MS = 400;

type OrderDetailsRedirectRouter = Pick<Router, 'back' | 'canDismiss' | 'dismissTo' | 'replace'>;

const isCartDestination = (url: string) => url === routes.cart || url === routes.cartAddExtras;

export const isOrderDetailsModalPath = (pathname: string) => pathname.startsWith('/orders/');

/**
 * Whether a redirect has to dismiss the iOS order details sheet first. Only then
 * does the native dismissal need to finish before further UI is shown.
 */
export const shouldLeaveIosOrderDetailsModal = (
  router: Pick<Router, 'canDismiss'>,
  pathname: string,
  isIos: boolean
) => isIos && isOrderDetailsModalPath(pathname) && router.canDismiss();

/**
 * Order Details is an iOS sheet stacked on the account drawer, which is itself a
 * root modal. A single `dismissTo(cart)` only replaces the sheet and leaves the
 * drawer in the stack, so the sheet is popped first and, once its dismissal
 * finishes, `dismissTo(cart)` replaces the drawer with the cart on the root stack.
 *
 * When every entry is unavailable, go back and make sure the order history is shown.
 * `onComplete` runs once navigation has been issued and, on iOS, after the sheet
 * dismissal delay.
 *
 * `getPathname` must read the router's live pathname from the router hooks (e.g. via a
 * ref over `usePathname()`): `getStaticPathname()` does not account for the iOS sheet.
 */
export function redirectFromOrderDetailsModal(
  router: OrderDetailsRedirectRouter,
  url: string,
  isIos: boolean,
  getPathname: () => string,
  onComplete?: () => void
): void {
  const pathname = getPathname();

  if (!shouldLeaveIosOrderDetailsModal(router, pathname, isIos)) {
    // Replacing the screen we are already on remounts it, e.g. reordering from the
    // order list would show its unavailable-items modal a second time.
    if (pathname !== url) {
      router.replace(url);
    }
    onComplete?.();
    return;
  }

  if (isCartDestination(url)) {
    // Pop the sheet first: a single dismissTo(cart) only replaces the top route
    // and leaves the account drawer (order list) in the stack.
    router.back();
    setTimeout(() => {
      router.dismissTo(url);
      onComplete?.();
    }, IOS_MODAL_DISMISS_DELAY_MS);
    return;
  }

  if (url === routes.orders) {
    router.back();
    setTimeout(() => {
      // Order details can also be opened from other screens (e.g. loyalty history
      // or a deep link), so make sure the order list is what ends up visible.
      if (getPathname() !== routes.orders) {
        router.dismissTo(url);
      }
      onComplete?.();
    }, IOS_MODAL_DISMISS_DELAY_MS);
    return;
  }

  router.replace(url);
  onComplete?.();
}
